import { normalizeDrivingRouteGroupKey } from "@utils/mode_tool/modeRoute";
import { isGeneratedTurnByTurnPoint } from "@utils/mode_tool/turnByTurn";

export function buildModePointsSignature(pointsList = []) {
  return (Array.isArray(pointsList) ? pointsList : [])
    .filter((point) => !isGeneratedTurnByTurnPoint(point))
    .map((item) =>
      [
        String(item?.id || ""),
        String(item?.latitude ?? ""),
        String(item?.longitude ?? ""),
        normalizeDrivingRouteGroupKey(
          item?.routeGroup ?? item?.route_group ?? "",
          item?.routeGroupName ?? item?.route_group_name ?? "",
          Number(item?.number_point || item?.order || 0) || 0,
        ),
      ].join(":"),
    )
    .sort((first, second) => first.localeCompare(second))
    .join("|");
}

export function buildWalkingRouteShapePointsSignature(shapePoints = []) {
  return (Array.isArray(shapePoints) ? shapePoints : [])
    .map((point) => {
      const lng = Number(point?.longitude ?? point?.lng);
      const lat = Number(point?.latitude ?? point?.lat);
      return [
        point?.id || "",
        Number(point?.afterPointOrder || 0) || 0,
        Number.isFinite(lng) ? lng.toFixed(7) : "",
        Number.isFinite(lat) ? lat.toFixed(7) : "",
      ].join(":");
    })
    .join("|");
}

export function buildWalkingRouteSignature(pointsList = [], shapePoints = []) {
  return `${buildModePointsSignature(pointsList)}::shape::${buildWalkingRouteShapePointsSignature(shapePoints)}`;
}

export function buildModeRouteRefreshSignature(pointsList = [], shapePoints = []) {
  const generatedDirectionSignature = (Array.isArray(pointsList) ? pointsList : [])
    .filter((point) => isGeneratedTurnByTurnPoint(point))
    .map((point) => [
      String(point?.id || ""),
      String(point?.number_point ?? point?.order ?? ""),
      String(point?.latitude ?? point?.lat ?? ""),
      String(point?.longitude ?? point?.lng ?? ""),
      String(point?.turnCase ?? point?.turn_case ?? point?.turnType ?? ""),
    ].join(":"))
    .sort((first, second) => first.localeCompare(second))
    .join("|");
  return `${buildWalkingRouteSignature(pointsList, shapePoints)}::directions::${generatedDirectionSignature}`;
}

export function getRouteShapePointAfterOrder(point = {}) {
  return Number(point?.afterPointOrder ?? point?.after_point_order) || 1;
}

// Point numbers can change after insertion/reordering. The stored owner ID
// remains authoritative; the numeric anchor is only for legacy records.
export function resolveRouteShapePointOwners(shapePoints = [], routePoints = []) {
  const byId = new Map(routePoints.map((point) => [String(point.id || ""), point]));
  const byOrder = new Map(routePoints.map((point) => [
    Number(point.number_point ?? point.order), point,
  ]));
  return shapePoints.flatMap((shape) => {
    const pointId = String(shape.pointId ?? shape.point_id ?? "");
    const owner = pointId
      ? byId.get(pointId)
      : byOrder.get(getRouteShapePointAfterOrder(shape));
    if (!owner) return [];
    return [{
      ...shape,
      pointId: String(owner.id || ""),
      afterPointOrder: Number(owner.number_point ?? owner.order),
    }];
  });
}

export function readRouteShapePointSegmentRatio(point = {}) {
  const rawRatio = point?.segmentRatio ?? point?.segment_ratio;
  if (rawRatio === undefined || rawRatio === null || rawRatio === "") {
    return null;
  }
  const ratio = Number(rawRatio);
  return Number.isFinite(ratio) ? ratio : null;
}

export function getRouteShapePointSegmentRatio(point = {}) {
  const ratio = readRouteShapePointSegmentRatio(point);
  if (ratio !== null) return ratio;
  const orderFallback = Number(point?.order);
  return Number.isFinite(orderFallback) ? orderFallback : 0;
}

export function normalizeRouteShapePointOrders(shapePoints = []) {
  const sorted = [...(Array.isArray(shapePoints) ? shapePoints : [])].sort(
    (a, b) => {
      const afterDiff =
        getRouteShapePointAfterOrder(a) - getRouteShapePointAfterOrder(b);
      if (afterDiff) return afterDiff;
      const ratioDiff =
        getRouteShapePointSegmentRatio(a) - getRouteShapePointSegmentRatio(b);
      if (ratioDiff) return ratioDiff;
      return Number(a?.order || 0) - Number(b?.order || 0);
    },
  );
  const pointsBySegment = sorted.reduce((acc, point) => {
    const segmentKey = String(getRouteShapePointAfterOrder(point));
    const current = acc.get(segmentKey) || [];
    current.push(point);
    acc.set(segmentKey, current);
    return acc;
  }, new Map());

  return sorted.map((point, index) => {
    const segmentKey = String(getRouteShapePointAfterOrder(point));
    const siblings = pointsBySegment.get(segmentKey) || [];
    const pointRatio = readRouteShapePointSegmentRatio(point);
    const siblingIndex = Math.max(
      0,
      siblings.findIndex((sibling) => sibling === point),
    );
    const fallbackRatio =
      siblings.length > 1 ? (siblingIndex + 1) / (siblings.length + 1) : 0.5;

    return {
      ...point,
      segmentRatio: pointRatio === null ? fallbackRatio : pointRatio,
      order: index + 1,
    };
  });
}

export function normalizeRouteShapePointsFromApi(shapePoints = []) {
  return normalizeRouteShapePointOrders(
    (Array.isArray(shapePoints) ? shapePoints : []).map((point, index) => {
      const segmentRatio = readRouteShapePointSegmentRatio(point);
      const hasUsableSegmentRatio =
        segmentRatio !== null && segmentRatio > 0 && segmentRatio < 1;
      return {
        id: String(point?.id || `shape-${index + 1}`),
        latitude: Number(point?.latitude ?? point?.lat),
        longitude: Number(point?.longitude ?? point?.lng),
        afterPointOrder:
          Number(point?.afterPointOrder ?? point?.after_point_order) || 1,
        pointId: String(point?.pointId ?? point?.point_id ?? ""),
        labelCode: String(point?.labelCode ?? point?.label_code ?? ""),
        ...(hasUsableSegmentRatio ? { segmentRatio } : {}),
        order: Number(point?.order || index + 1) || index + 1,
      };
    }),
  );
}

export function buildRouteSegmentCoordinates(pointsList = []) {
  return (Array.isArray(pointsList) ? pointsList : [])
    .map((point) => ({
      order: Number(point?.number_point || point?.order || 0) || 0,
      lng: Number(point?.longitude ?? point?.lng),
      lat: Number(point?.latitude ?? point?.lat),
      id: String(point?.id || ""),
      labelCode: String(point?.labelCode || point?.label_code || ""),
    }))
    .filter(
      (point) =>
        point.order > 0 &&
        Number.isFinite(point.lng) &&
        Number.isFinite(point.lat),
    )
    .sort((a, b) => a.order - b.order);
}

export function calculateRouteSegmentRatio(lngLat = {}, pointsList = [], order = 1) {
  const coordinates = buildRouteSegmentCoordinates(pointsList);
  const segmentIndex = coordinates.findIndex(
    (point) => Number(point.order) === Number(order),
  );
  if (segmentIndex < 0 || segmentIndex >= coordinates.length - 1) return 0;
  const start = coordinates[segmentIndex];
  const end = coordinates[segmentIndex + 1];
  const target = {
    lng: Number(lngLat?.longitude ?? lngLat?.lng),
    lat: Number(lngLat?.latitude ?? lngLat?.lat),
  };
  if (!Number.isFinite(target.lng) || !Number.isFinite(target.lat)) return 0;
  const dx = end.lng - start.lng;
  const dy = end.lat - start.lat;
  const lengthSquared = dx * dx + dy * dy || 1;
  return Math.max(
    0,
    Math.min(
      1,
      ((target.lng - start.lng) * dx + (target.lat - start.lat) * dy) /
        lengthSquared,
    ),
  );
}

export function calculateNearestRouteSegmentInfo(lngLat = {}, pointsList = []) {
  const coordinates = buildRouteSegmentCoordinates(pointsList);
  const fallback = {
    afterPointOrder: coordinates[0]?.order || 1,
    pointId: coordinates[0]?.id || "",
    labelCode: coordinates[0]?.labelCode || "",
    segmentRatio: 0,
  };
  if (coordinates.length < 2) return fallback;
  const target = {
    lng: Number(lngLat?.longitude ?? lngLat?.lng),
    lat: Number(lngLat?.latitude ?? lngLat?.lat),
  };
  if (!Number.isFinite(target.lng) || !Number.isFinite(target.lat)) {
    return fallback;
  }
  let nearestInfo = { ...fallback };
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const start = coordinates[index];
    const end = coordinates[index + 1];
    const dx = end.lng - start.lng;
    const dy = end.lat - start.lat;
    const lengthSquared = dx * dx + dy * dy || 1;
    const t = Math.max(
      0,
      Math.min(
        1,
        ((target.lng - start.lng) * dx + (target.lat - start.lat) * dy) /
          lengthSquared,
      ),
    );
    const projectedLng = start.lng + t * dx;
    const projectedLat = start.lat + t * dy;
    const distance =
      (target.lng - projectedLng) * (target.lng - projectedLng) +
      (target.lat - projectedLat) * (target.lat - projectedLat);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestInfo = {
        afterPointOrder: start.order,
        pointId: start?.id || "",
        labelCode: start?.labelCode || "",
        segmentRatio: t,
      };
    }
  }
  return nearestInfo;
}

export function calculateNearestRouteSegmentOrder(lngLat = {}, pointsList = []) {
  const segmentInfo = calculateNearestRouteSegmentInfo(lngLat, pointsList);
  return Number(segmentInfo?.afterPointOrder || segmentInfo || 1) || 1;
}
